import MaterialTable from '@material-table/core';
import { Button } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { useRouterStore } from 'mobx-state-router';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from 'src/common/ui/components/Alert';
import ChartActivity from 'src/components/pages/patientActivity/ChartActivity';
import EventDetail from 'src/components/pages/patientActivity/EventDetail';
import { PatientActivityIcon } from 'src/components/pages/patientActivity/PatientActivityIcon';
import { PatientExportDialog } from 'src/components/pages/patientActivity/PatientExportDialog';
import PebbleDetail from 'src/components/pages/patientActivity/PebbleDetail';
import TaskDetail from 'src/components/pages/patientActivity/TaskDetail';
import ViewsButtonGroup, {
  ACTIVITY_VIEW_QUERY_PARAM,
} from 'src/components/pages/patientActivity/ViewsButtonGroup';
import { filterActivityData } from 'src/components/pages/patientActivity/filterActivityData';
import { View, ActivityType } from 'src/components/pages/patientActivity/types';
import type { ActivityData } from 'src/components/pages/patientActivity/types';
import useSWRActivityEvents from 'src/components/pages/patientActivity/useSWRActivityEvents';
import useSWRActivityPebbles from 'src/components/pages/patientActivity/useSWRActivityPebbles';
import useSWRActivityTasks from 'src/components/pages/patientActivity/useSWRActivityTasks';
import { PatientShowContext } from 'src/components/pages/patientShow/PatientShowContext';
import { FlowMappingsContextProvider } from 'src/nightingale/components/Flow/FlowMappingsContext';
import { PageTitle } from 'src/nightingale/components/common/PageTitle/PageTitle';
import { InteractionKind } from 'src/nightingale/types/types';
import { getExactFullName } from 'src/shared/stores/resource';
import { UNSCHEDULED_EVENT_STATUSES } from 'src/shared/util/events';
import { getCalendarEventStyles } from 'src/util/calendar';

const PatientActivity: React.VFC<{
  patientId: string;
}> = ({ patientId }) => {
  // Look for a query param designating the activity view to display, or use the default
  const { routerState } = useRouterStore();
  const viewQueryParam: View | undefined = routerState.queryParams[ACTIVITY_VIEW_QUERY_PARAM];
  const [view, setView] = useState<View>(viewQueryParam || View.Default);
  const [finishedRenderingData, setFinishedRenderingData] = useState<boolean>(false);
  const [exportModalShowing, setExportModalShowing] = useState<boolean>(false);

  const classes = useStyles();
  const { urlFragment } = useContext(PatientShowContext);

  const { data: events, error: eventsError } = useSWRActivityEvents(patientId);
  const { data: tasks, error: tasksError } = useSWRActivityTasks(patientId);
  const { data: pebbles, error: pebblesError } = useSWRActivityPebbles(patientId);

  const isLoading = !events || !tasks || !pebbles;

  const activityData: ActivityData[] = [
    ...(events || []),
    ...(tasks || []),
    ...(pebbles || []),
  ].sort((a, b) => (a.sortDateEpoch < b.sortDateEpoch ? 1 : -1));

  const filteredActivityData = useMemo(
    () => filterActivityData(activityData, view),
    [activityData, view],
  );

  const tableRef = useRef(null);
  const lastExpandedFragmentRef = useRef<string | null>(null);

  useEffect(() => {
    // Used to determine when to attempt automatic scrolling to a linked item. Using `isLoading` directly
    // could lead to buggy behavior, because `isLoading` could be false (all of the data is available),
    // but the data has not yet been rendered. This ensures that the data is rendered and will be found
    // when querying for the item.
    setFinishedRenderingData(!isLoading);
  }, [isLoading]);

  // Expand the row with the linked item. Scrolling to it is handled in the ChartActivity component.
  useEffect(() => {
    if (isLoading || !urlFragment || !tableRef.current) return undefined;
    if (lastExpandedFragmentRef.current === urlFragment) return undefined;

    const dashIndex = urlFragment.indexOf('-');
    const idToExpand = dashIndex !== -1 ? urlFragment.slice(dashIndex + 1) : null;
    if (!idToExpand) return undefined;

    const index = findRowIndexForUrlItem(filteredActivityData, idToExpand);
    if (index === -1) return undefined;

    // Small delay to ensure the table has rendered
    const timeout = setTimeout(() => {
      if (!tableRef.current) return;

      // @ts-expect-error MaterialTable internal API
      const renderData = tableRef.current.state?.renderData;
      const rowData = renderData?.[index];
      if (!rowData || rowData.tableData?.showDetailPanel) return;

      const path = [rowData.tableData?.index ?? index];
      // @ts-expect-error MaterialTable internal API
      const detailPanel = tableRef.current.props.detailPanel[0];
      const renderFn = typeof detailPanel === 'function' ? detailPanel : detailPanel.render;

      // @ts-expect-error MaterialTable internal API
      tableRef.current.onToggleDetailPanel(path, renderFn);
      lastExpandedFragmentRef.current = urlFragment;
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading, urlFragment, filteredActivityData]);

  // Shrink things to better fit side-by-side.
  const cellStyle = { padding: '0.5vw', fontSize: '14px' };

  return (
    <>
      <div className={classes.header}>
        <PageTitle>
          Activity{' '}
          <Button onClick={() => setExportModalShowing(true)} title="Generate chart export">
            <GetAppIcon />{' '}
          </Button>
        </PageTitle>
        <ViewsButtonGroup view={view} setView={setView} />
      </div>
      {eventsError || tasksError || pebblesError ? (
        <Alert
          title="Error loading patient activity"
          error={eventsError || tasksError || pebblesError}
          severity="error"
        />
      ) : null}
      <ChartActivity finishedRenderingData={finishedRenderingData}>
        <FlowMappingsContextProvider kind={InteractionKind.Pebble}>
          <MaterialTable<ActivityData>
            tableRef={tableRef}
            data={filteredActivityData}
            columns={[
              {
                title: 'Date',
                field: 'start',
                cellStyle,
                filtering: false,
                render: rowData => (
                  <div
                    className={
                      UNSCHEDULED_EVENT_STATUSES.includes(rowData.status)
                        ? classes.strikethrough
                        : undefined
                    }
                    data-activity-item-id={getItemIdForScroll(rowData)}
                  >
                    {rowData.start}
                  </div>
                ),
              },
              {
                title: 'Type',
                field: 'label',
                cellStyle,
                render: rowData => (
                  <div
                    className={classNames(
                      classes.type,
                      UNSCHEDULED_EVENT_STATUSES.includes(rowData.status)
                        ? classes.strikethrough
                        : null,
                    )}
                  >
                    <PatientActivityIcon type={rowData.type} />
                    {rowData.label}
                  </div>
                ),
              },
              {
                title: 'Providers',
                field: 'attendees',
                cellStyle,
                render: rowData => <div>{rowData.attendees.map(getExactFullName).join(', ')}</div>,
              },
              {
                title: 'Summary',
                field: 'eventNotes',
                cellStyle,
                filtering: false,
                width: '25%',
                render: rowData => <div className={classes.eventNotes}>{rowData.eventNotes}</div>,
              },
            ]}
            detailPanel={[
              {
                tooltip: 'Details',
                render: ({ rowData }) => {
                  switch (rowData.type) {
                    case ActivityType.Event:
                      return (
                        <EventDetail
                          eventId={rowData.eventId as string}
                          patientId={patientId}
                          classes={classes}
                        />
                      );
                    case ActivityType.Task:
                      return (
                        <TaskDetail
                          taskId={rowData.taskId}
                          name={rowData.name as string}
                          patientId={patientId}
                          classes={classes}
                        />
                      );
                    case ActivityType.Pebble:
                      return (
                        <PebbleDetail
                          pebbleId={rowData.pebbleId}
                          classes={classes}
                          canEditInteraction={false}
                        />
                      );
                    default:
                      return null;
                  }
                },
              },
            ]}
            isLoading={isLoading}
            onRowClick={(event, rowData, togglePanel) => togglePanel?.()}
            options={{
              toolbar: false,
              paging: false,
              filtering: false,
              search: false,
              headerStyle: { display: 'default' },
              sorting: false,
              rowStyle: rowData => {
                const style = getCalendarEventStyles(rowData.eventStatusInfo);
                delete style.textDecoration;
                return { ...style, borderLeftWidth: '8px' } as React.CSSProperties;
              },
            }}
          />
        </FlowMappingsContextProvider>
      </ChartActivity>

      <PatientExportDialog
        isOpen={exportModalShowing}
        close={() => setExportModalShowing(false)}
        patientId={patientId}
      />
    </>
  );
};

/** Returns the formatted activity item id needed for automatic scrolling to a linked item. */
function getItemIdForScroll(rowData: ActivityData) {
  const anchor = 'activity';
  switch (rowData.type) {
    case ActivityType.Event:
      return `${anchor}-${rowData.eventId}`;
    case ActivityType.Task:
      return `${anchor}-${rowData.taskId}`;
    case ActivityType.Pebble:
      return `${anchor}-${rowData.pebbleId}`;
    default:
      return '';
  }
}
function findRowIndexForUrlItem(tableData: ActivityData[], idFromUrl: string) {
  return tableData.findIndex(rowData => {
    switch (rowData.type) {
      case ActivityType.Event:
        return idFromUrl === rowData.eventId;
      case ActivityType.Task:
        return idFromUrl === rowData.taskId;
      case ActivityType.Pebble:
        return idFromUrl === rowData.pebbleId;
      default:
        return false;
    }
  });
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  type: {
    fontWeight: 'bold',
  },
  strikethrough: {
    textDecoration: 'line-through',
  },
  eventNotes: {
    fontStyle: 'italic',
    textDecoration: 'none',
    maxWidth: 400,
  },
  summary: {
    padding: 15,
  },
});

export default PatientActivity;
